import { definePage, defineResource } from '@restavo/crud';
import { AxiosError } from 'axios';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@carbon/react';
import { Renew } from '@carbon/react/icons';
import EmptyState from 'src/components/Base/EmptyState';
import InventoryItemForm from 'src/components/Inventory/InventoryItemForm';
import InventoryMovementForm from 'src/components/Inventory/InventoryMovementForm';
import StockCell from 'src/components/Inventory/StockCell';
import { useInventoryStockLevels } from 'src/hooks/useInventoryItems';
import { useCreateInventoryMovementMutation } from 'src/hooks/useInventoryMovements';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import {
  tenantInventoryCategories,
  tenantInventoryItemCreate,
  tenantInventoryItemDelete,
  tenantInventoryItems,
  tenantInventoryItemUpdate,
} from 'src/services/apis/tenantRequests';
import { InventoryItemEntity } from 'src/services/apis/types';
import { formatQuantity, InventoryItemStatusEnum } from 'src/utils/inventory';
import { getMutationErrorMessage } from 'src/utils/error';
import { QUERIES } from 'src/utils/react-query';

const inventoryItems = defineResource({
  key: QUERIES.INVENTORY_ITEMS,
  // The hand-written page fetched page 1 with limit 20; the reach stays identical.
  pageSize: 20,
  api: {
    list: tenantInventoryItems,
    create: tenantInventoryItemCreate,
    update: tenantInventoryItemUpdate,
    remove: tenantInventoryItemDelete,
  },
  search: 'server',
  searchFields: ['name', 'description'],
  // Spec §7.1. The category filter and the category column share one reference load, so
  // their options fetch once between them (spec §7.5).
  filterable: [
    {
      field: 'status',
      labelKey: 'common.status',
      type: 'enum',
      options: [
        { id: String(InventoryItemStatusEnum.ACTIVE), labelKey: 'inventoryItemStatuses.active' },
        { id: String(InventoryItemStatusEnum.ARCHIVED), labelKey: 'inventoryItemStatuses.archived' },
      ],
    },
    {
      field: 'categoryId',
      labelKey: 'common.category',
      type: 'reference',
      load: tenantInventoryCategories,
      optionLabel: (category: any) => category.name,
    },
    { field: 'unitCost', labelKey: 'common.unitCost', type: 'number' },
    { field: 'updatedAt', labelKey: 'common.updated', type: 'date' },
  ],
  // Item mutations move stock balances and the dashboard's inventory panels.
  invalidates: [QUERIES.INVENTORY_STOCK_LEVELS, QUERIES.DASHBOARD_INVENTORY_REPORTS],
});

/**
 * The InventoryItems page config, as a hook because two of its pieces are page-specific
 * runtime data: the per-branch stock map behind the stock column, and the movements modal
 * the extra row action opens. Everything else is the resource config above.
 */
export const useInventoryItemsPage = () => {
  const { t } = useTranslation();
  const { data: stockLevels } = useInventoryStockLevels();
  const currentRestaurant = useCurrentRestaurant();
  const createMovementMutation = useCreateInventoryMovementMutation();
  const movementFormRef = useRef<any>();
  const [movementItem, setMovementItem] = useState<null | InventoryItemEntity>(null);

  const handleCloseMovement = React.useCallback(() => setMovementItem(null), []);

  const handleCreateMovement = React.useCallback(
    async (values) => {
      await createMovementMutation.mutateAsync({
        ...values,
        quantity: Number(values.quantity),
        date: new Date(values.date).toISOString(),
      });
      movementFormRef.current?.resetForm();
      setMovementItem(null);
    },
    [createMovementMutation]
  );

  const stockMap = Object.fromEntries((stockLevels || []).map((level) => [level.itemId, level.quantity]));
  const getUnitText = (unit: string) => {
    const key = `inventoryUnits.${unit}`;
    return t(key) === key ? unit : t(key);
  };
  const title = t('common.inventoryItems');

  const page = {
    ...definePage(inventoryItems, {
      titleKey: 'titles.inventoryPageWithoutBranch',
      descriptionKey: 'common.inventoryItems',
      createLabelKey: 'actions.createNewInventoryItem',
      updateLabelKey: 'actions.updateInventoryItem',
      deleteHeadingKey: 'messages.areYouSureToDeleteThisInventoryItem',
      columns: [
        { key: 'name', headerKey: 'common.name', sortable: true, alwaysVisible: true },
        {
          key: 'category',
          headerKey: 'common.category',
          display: {
            kind: 'reference',
            load: tenantInventoryCategories,
            optionLabel: (category: any) => category.name,
          },
        },
        { key: 'unit', headerKey: 'common.unit', render: (row: InventoryItemEntity) => getUnitText(row.unit) },
        // currency kills the NaNK here too: an empty unitCost renders empty, never "NaNK".
        { key: 'unitCost', headerKey: 'common.unitCost', display: { kind: 'currency' } },
        {
          key: 'currentStock',
          headerKey: 'common.currentStock',
          render: (row: InventoryItemEntity) => (
            <StockCell unit={row.unit} stock={stockMap[row.id] ?? 0} threshold={row.lowStockThreshold} />
          ),
        },
        {
          key: 'lowStockThreshold',
          headerKey: 'common.lowStockThreshold',
          render: (row: InventoryItemEntity) =>
            row.lowStockThreshold != null ? formatQuantity(row.lowStockThreshold, row.unit) : '',
        },
        {
          key: 'status',
          headerKey: 'common.status',
          display: {
            kind: 'tag',
            map: {
              [InventoryItemStatusEnum.ACTIVE]: { labelKey: 'inventoryItemStatuses.active', type: 'cyan' },
              [InventoryItemStatusEnum.ARCHIVED]: { labelKey: 'inventoryItemStatuses.archived', type: 'gray' },
            },
          },
        },
        // Not on the hand-written page, but the updatedAt filter deserves its column; hidden
        // by default, one toggle away, exactly like the categories page's.
        { key: 'updatedAt', headerKey: 'common.updated', display: { kind: 'date' }, defaultHidden: true, sortable: true },
      ],
      form: InventoryItemForm,
      emptyState: <EmptyState />,
      toPayload: (values: any, params) => ({
        ...values,
        restaurantId: params.restaurantId,
        tags: values.tags?.split(',').map((tag: string) => tag.trim()) || [],
        unitCost: values.unitCost === '' ? null : Number(values.unitCost),
        lowStockThreshold: values.lowStockThreshold === '' ? null : Number(values.lowStockThreshold),
      }),
      useTitleVars: () => ({
        restaurantName: currentRestaurant?.name,
        title: t('common.inventoryItem'),
        titlePlural: t('common.inventoryItems'),
      }),
    }),
    toolbar: (state: { refetch: () => void }) => (
      <Button
        renderIcon={Renew}
        onClick={state.refetch}
        hasIconOnly
        kind="secondary"
        iconDescription={t('common.refresh')}
      />
    ),
    extraActions: [{ labelKey: 'actions.recordMovement', onClick: (row: InventoryItemEntity) => setMovementItem(row) }],
  };

  const movementsModal = (
    <Modal
      open={!!movementItem}
      size={'sm'}
      modalHeading={t('actions.recordMovement')}
      modalLabel={t('titles.inventoryPageWithoutBranch', {
        restaurantName: currentRestaurant?.name,
        titlePlural: title,
      })}
      primaryButtonText={
        createMovementMutation.isLoading ? `${t('common.loading')}...` : t('actions.recordMovement')
      }
      secondaryButtonText={t('actions.cancel')}
      onSecondarySubmit={handleCloseMovement}
      onRequestClose={handleCloseMovement}
      primaryButtonDisabled={createMovementMutation.isLoading}
      onRequestSubmit={() => movementFormRef.current?.submitForm()}
    >
      <div style={{ minHeight: 500 }}>
        {!!movementItem && (
          <InventoryMovementForm
            errorMessage={getMutationErrorMessage(createMovementMutation.error as AxiosError)}
            presetItemId={movementItem?.id}
            ref={movementFormRef}
            onSubmit={handleCreateMovement}
          />
        )}
      </div>
    </Modal>
  );

  return { page, movementsModal };
};
